import { CareRecipientType, QuestionResponse } from '@karehero/models';
import {
  CareCircleCreate as CareCircleCreatePage,
  type CareCircleSpec,
} from '@karehero/llama/pwa/pages/CareCircleCreate/CareCircleCreate';
import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { useGoBack } from 'hooks/useGoBack';
import { ROUTES } from 'router';
import { dispatch } from 'store';
import { useCreateCareCircleMutation } from 'store/api/careCircle';
import { getAccount } from 'store/slices/iamSlice';
import { newToast } from 'store/slices/uiSlice';

// Wrapped for the admin page with a different consent text.
export const AdminCareCircleCreate = () => {
  return <CareCircleCreate isAdmin={true} />;
};

export const CareCircleCreate = ({
  isAdmin = false,
}: {
  isAdmin?: boolean;
}) => {
  // load target account ID from URL params if set; fallback to current account ID and then to login page.
  let { accountID } = useParams();
  if (!accountID) {
    accountID = useSelector(getAccount)?.id;
    if (!accountID) {
      return <Navigate to={ROUTES.SIGN_IN} />;
    }
  }

  const navigate = useNavigate();
  const goBack = useGoBack();
  const [createCareCircle] = useCreateCareCircleMutation();

  const createCareCircleCallback = useCallback(
    async (value: CareCircleSpec) => {
      console.log('[CareCircleCreate] creating care circle with value', value);

      const triageResponses: QuestionResponse[] = [];
      if (value.careRecipientType === CareRecipientType.Other) {
        triageResponses.push({
          questionID: 'care-recipient-first-name-v1',
          response: value.careRecipientName,
        });
      }
      triageResponses.push(
        {
          questionID: 'care-recipient-consent-v1',
          response: value.careRecipientConsent.toString(),
        },
        {
          questionID: 'care-recipient-symptoms-and-conditions-v1',
          response: value.careRecipientSymptomsAndConditions,
        },
        {
          questionID: 'care-recipient-date-of-birth-v1',
          response: value.careRecipientDateOfBirth,
        },
        {
          questionID: 'care-recipient-postcode-v1',
          response: value.careRecipientPostcode,
        },
      );

      const careCircle = await createCareCircle({
        primaryCaregiverAccountId: accountID,
        careRecipientType: value.careRecipientType,
        careRecipientName: value.careRecipientName,
        triageResponses: triageResponses,
      });
      if (!('data' in careCircle)) {
        return;
      }
      const careCircleID = careCircle.data!.id;
      console.log(
        '[CareCircleCreate] care circle created with ID',
        careCircleID,
      );
      await dispatch(
        newToast({ title: 'Care circle created', variant: 'success' }),
      );
      goBack();
    },
    [createCareCircle, navigate, accountID],
  );

  return (
    <CareCircleCreatePage
      onBack={goBack}
      onSave={createCareCircleCallback}
      onCancel={goBack}
      showToast={(title, description, variant) =>
        dispatch(newToast({ title, description, variant }))
      }
      isAdmin={isAdmin}
    />
  );
};

export default CareCircleCreate;
